import { useState } from "react";
import toast from "react-hot-toast";
import { V3ModalBase } from "../V3ModalBase";
import { UACGenericOptionsScreen } from "./UADGenericOptionsScreen";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { V3ModalUACSpotify } from "./V3ModalUACSpotify";
import { AddSource, AddSourceWizardStage } from "./AddSource/AddSource";
import {
    Collection,
    CollectionRuleResponse,
    ContentResponse,
    FindContentMetadataResponse,
    FindDuplicatesResponse,
    ImportResponse,
    WebsiteResponse,
    CollectionRuleType,
    RuleOperator,
    SourceType,
    ContentType,
    CreateMediaUploadBodyParams,
    MediaUploadResponse,
    MediaUsageResponse,
    SubscriptionPlanHelpers,
    SubscriptionPlanKey,
    AccessLevel
} from "@authory-packages/core";
import { AddSourceFormik } from "./AddSource/AddSource.utils";
import { AliasesFormik } from "../EditAliases";
import { AddContent, AddContentStageChange, AddContentWizardStage } from "./AddContent/AddContent";
import { AddContentUpload } from "./AddContentUpload";
import { ManualImportPathTaken, UniversalAddPickType, UniversalAddTrackingEventNames } from "./UniversalAddContentTracking";
import { V3IconArticle, V3IconBook, V3IconImage, V3IconPDF, V3IconPodcast, V3IconPost, V3IconVideo, V3IconWeb, V3IconWebsiteEmail } from "../Icons";
import { getMaxLimitUsageMessage, getModalUACTTitle, getPaywallModalDescription, getPaywallModalTitle } from "./V3ModalUAC.utils";
import { AddContentAddURL } from "./AddContentAddUrl";
import { makeIdFromLength } from "../../utils/makeIdFromLength";
import { AddContentEmail } from "./Email/AddContentEmail";
import { AddContentEmailFinished } from "./Email/AddContentEmailFinished";
import { V3EditCollectionRules } from "./V3EditCollectionRules";
import { useRouter } from "next/router";
import { V3ModalIconSegmentVariant } from "../ReusableStyledComponents/ModalIconSegment";
import { V3ConfirmationModal } from "../V3ConfirmationModal";
import { AddContentImage } from "./AddContentImage";
import { ManualBulkSelectContainer } from "./ManualBulkSelectContent";
import { AutoUpdateFlow, AutoUpdateScreen, RulesAutoUpdateProps } from "./AutoUpdateScreen";
import { uniqBy } from "lodash";
import { FileUploadTarget, FinalImportResult, ImportItem, UnifiedAddContentStages } from "./AddContent/AddContent.types";
import { FindDuplicateParams } from "../../utils/findContentByURL";
import { RoundedBoxCTAProps } from "./SharedComponents/RoundedBoxCTA";
import { V3BodyGrey48 } from "../ReusableStyledComponents";
interface V3ModalUACProps {
    modalOpen: boolean;
    usedPlugin: boolean;
    collection?: Collection;
    rules: CollectionRuleResponse[];
    sources: WebsiteResponse[];
    names: string[];
    openingState: UnifiedAddContentStages;
    articleCount: number;
    subscriptionPlanKey: SubscriptionPlanKey | null;
    isAboveAudioLimit?: boolean;
    isAboveVideoLimit?: boolean;
    hasPaymentMethod: boolean;
    isOnTrial?: boolean;
    mediaUsage: MediaUsageResponse;
    numItems: number;
    initialSourceType?: SourceType;
    onCloseHandler: () => void;
    onAutommatedImportSubmit: (data: AddSourceFormik) => Promise<WebsiteResponse | undefined>;
    onSourceExists: (slug: string) => Promise<void>;
    onEmailSourceSubmit: (name: string) => Promise<WebsiteResponse | undefined>;
    onAliasesSubmit: (data: AliasesFormik) => Promise<void>;
    trackEvent: (eventName: string, data: any) => void;
    onFindContent: (url: string, mimeType?: string) => Promise<ImportResponse | undefined>;
    onWebsiteImport: (url: string) => Promise<ImportResponse | undefined>;
    onFindContentSnippets: (url: string) => Promise<FindContentMetadataResponse | undefined>;
    onFindDuplicate: (params: FindDuplicateParams) => Promise<FindDuplicatesResponse | undefined>;
    onReplaceDuplicate: (item: ContentResponse) => Promise<ContentResponse | undefined>;
    onManualImportSubmit: (data: FinalImportResult) => Promise<ContentResponse | undefined>;
    onFileChoosenHandler: (file: File) => Promise<ImportResponse> | undefined;
    onTemporaryImageUploadHandler: (file: File) => Promise<string>;
    onGetMediaUploadSignedUrl: (data: CreateMediaUploadBodyParams) => Promise<MediaUploadResponse | undefined>;
    onRulesUpdate?: (operator: RuleOperator, rules: CollectionRuleResponse[]) => Promise<Collection | undefined>;
}

export const V3ModalUAC = ({
    modalOpen,
    sources,
    names,
    collection,
    rules,
    usedPlugin,
    openingState,
    isAboveAudioLimit,
    isAboveVideoLimit,
    isOnTrial,
    mediaUsage,
    numItems,
    articleCount,
    subscriptionPlanKey,
    initialSourceType = SourceType.Web,
    hasPaymentMethod,
    onCloseHandler,
    onAutommatedImportSubmit,
    onSourceExists,
    onEmailSourceSubmit,
    trackEvent,
    onAliasesSubmit,
    onFindContent,
    onWebsiteImport,
    onFindContentSnippets,
    onFindDuplicate,
    onReplaceDuplicate,
    onManualImportSubmit,
    onFileChoosenHandler,
    onTemporaryImageUploadHandler,
    onRulesUpdate,
    onGetMediaUploadSignedUrl
}: V3ModalUACProps) => {
    const router = useRouter();
    const isContent = router.route === APPLICATION_ROUTES.CONTENT;

    const [modalsubLabel, setModalsubLabel] = useState<string | undefined>();
    const [showPlanLimit, setShowPlanLimit] = useState(
        isContent && !SubscriptionPlanHelpers.respectsPlanLimitations(articleCount + 1, subscriptionPlanKey)
    );
    const [showAboveVideoLimit, setShowAboveVideoLimit] = useState(false);
    const [showAboveAudioLimit, setShowAboveAudioLimit] = useState(false);
    const [showMediaUploadPaywall, setMediaUploadPaywall] = useState(false);
    const [manualImportFlowExitDialog, setManualImportFlowExitDialog] = useState(false);
    const [stage, setStage] = useState(openingState);
    const [stageHistory, setStageHistory] = useState<UnifiedAddContentStages[]>([]);
    const [itemAdded, setItemAdded] = useState<ContentResponse[]>([]);
    const [newSources, setNewSources] = useState<string[]>([]);
    const [autoRulesSaved, setAutoRulesSaved] = useState(false);
    const [autoUpdateStepCompleted, setAutoUpdateStepCompleted] = useState(false);
    const [sourceType, setSourceType] = useState(initialSourceType);
    const [fileUploadTarget, setFileUploadTarget] = useState(FileUploadTarget.PDF);
    const [isPDFURLImport, setIsPDFURLImport] = useState(false);
    const [isSnippetImport, setIsSnippetImport] = useState(false);
    const [targetContentType, setTargetContentType] = useState(ContentType.Article);
    const [importPathTaken, setImportPathTaken] = useState<ManualImportPathTaken>(ManualImportPathTaken.Articles);
    const [suggestedUrl, setSuggestedUrl] = useState("");
    const [importItems, setImportItems] = useState<ImportItem[]>([]);
    const [emailSourceSlug, setEmailSourceSlug] = useState("");
    const [rulesAutoUpdateProps, setRulesAutoUpdateProps] = useState<RulesAutoUpdateProps>({
        rules: [],
        target: AutoUpdateFlow.BulkAdd
    });
    const [hasRuleConflict, setHasRuleConflict] = useState(false);

    // Tracking and copy purposes
    const [addSourceStage, setAddSourceStage] = useState(AddSourceWizardStage.AddURL);
    const [addContentStage, setAddContentStage] = useState<AddContentStageChange>({
        stage: AddContentWizardStage.AddContentImport,
        isConfirmingItem: false,
        isCheckingDuplicate: false
    });

    const modalOnCloseHandler = () => {
        if (stage === UnifiedAddContentStages.AddManual && addContentStage.stage === AddContentWizardStage.AddContentProcess) {
            setManualImportFlowExitDialog(true);
        } else {
            onCloseHandler();
        }
    };

    const accessLevel = SubscriptionPlanHelpers.getPlanAccessLevel(subscriptionPlanKey);
    const autoImportSupported = accessLevel === AccessLevel.Pro || accessLevel === AccessLevel.Standard;
    const isPlanWithMedia = accessLevel === AccessLevel.Standard || accessLevel === AccessLevel.Pro;
    const allowMediaUploads = isPlanWithMedia && (hasPaymentMethod || !isOnTrial);

    const goToStage = (targetStage: UnifiedAddContentStages) => {
        setStageHistory(stageHistory.concat(stage));
        setStage(targetStage);
    };

    const goBack = () => {
        setStage(stageHistory.slice(-1)[0]);
        setStageHistory(stageHistory.slice(0, stageHistory.length - 1));
    };

    const navigateToAddManual = (pathTaken: ManualImportPathTaken, isSnippetImport: boolean, targetContentType: ContentType) => {
        setIsSnippetImport(isSnippetImport);
        setTargetContentType(targetContentType);
        setImportPathTaken(pathTaken);
        goToStage(UnifiedAddContentStages.AddManual);
    };

    const navigateToManualArticle = () => navigateToAddManual(ManualImportPathTaken.Articles, true, ContentType.Article);

    const podcastOptionsOnNext = () => {
        setSourceType(SourceType.PodcastRssFeed);
        goToStage(UnifiedAddContentStages.AddSource);
    };

    if (showPlanLimit) {
        return (
            <V3ConfirmationModal
                onSubmit={() => {
                    router.push(`${APPLICATION_ROUTES.SETTINGS_BILLING}?update=plan`);
                }}
                setModalOpen={() => {
                    if (isContent) {
                        onCloseHandler();
                    } else {
                        setShowPlanLimit(false);
                    }
                }}
                modalOpen={true}
                title={getPaywallModalTitle(stage)}
                btnLabel={"Upgrade my subscription"}
                useIcon
                variant={V3ModalIconSegmentVariant.warning}
                description={getPaywallModalDescription(stage, subscriptionPlanKey)}
            />
        );
    }

    const onAutoUpdateNextStep = (rulesUpdated: boolean) => {
        setAutoUpdateStepCompleted(true);

        if (rulesAutoUpdateProps.target === AutoUpdateFlow.BulkAdd) {
            onCloseHandler();
            if (!rulesUpdated) toast.success("Settings saved!");
        } else if (rulesAutoUpdateProps.target === AutoUpdateFlow.Source) {
            goToStage(UnifiedAddContentStages.AddSource);
        } else if (rulesAutoUpdateProps.target === AutoUpdateFlow.Item) {
            goToStage(UnifiedAddContentStages.AddManual);
        }
    };

    const isConfirmingItem = addContentStage.stage === AddContentWizardStage.AddContentProcess && addContentStage.isConfirmingItem;
    const isManualBulkAdd = stage === UnifiedAddContentStages.CollectionManualBulkAdd;

    const handleAutoSettingRulesForUser = async (
        targetRules: CollectionRuleResponse[],
        targetCollection: Collection,
        currentRules: CollectionRuleResponse[]
    ) => {
        // If  we can automate the set rules process for the user we do it, otherwise we forward him to the modal to update rules.
        // We can set rules automatically for the following scenarios:
        // 1) collection has no rules at all
        // 2) collection has an or operator and we're trying to add a single type of rules
        const collectionHasNoRules = targetCollection.ruleCount === 0;

        // Combined rules checks
        const numTargetRuleTypesToSet = uniqBy(targetRules, (rl) => rl.type).length;
        const combinedRules = [...targetRules, ...currentRules];
        const numTotalRuleTypesToSet = uniqBy(combinedRules, (rl) => rl.type).length;
        const isSingleTotalRuleType = numTotalRuleTypesToSet === 1;
        const collectionHasAnyOperator = collection?.operator === RuleOperator.Any;

        // If collection has no rules
        // OR it's a single rule type being set
        // OR we're just targetting a single rule type update for an ANY operator
        // then we can update automatically
        const canUpdateRulesAutomatically =
            collectionHasNoRules || isSingleTotalRuleType || (collectionHasAnyOperator && numTargetRuleTypesToSet === 1);

        if (canUpdateRulesAutomatically) {
            // We only set All operator in one condition: collection has no rules and user is settings multiple rules at once (i.e. bulk add flow with multiple filters)
            // otherwise we fallback to the colection operator - which should always be set - or last resort any operator
            const targetOperator = collectionHasNoRules && numTotalRuleTypesToSet > 1 ? RuleOperator.All : (collection?.operator ?? RuleOperator.Any);

            if (onRulesUpdate) {
                await onRulesUpdate(targetOperator, [...targetRules, ...rules]);
                setAutoRulesSaved(true);
                onAutoUpdateNextStep(true);
            }
        } else {
            setHasRuleConflict(true);
            goToStage(UnifiedAddContentStages.CollectionEditRules);
        }
    };

    const podcastOptions: RoundedBoxCTAProps[] = [
        {
            text: "I want to import an entire podcast series",
            secondaryText: (
                <>
                    Authory will automagically find and import your episodes, even if it's many 1000's <span style={{ fontSize: 20 }}>✨</span>
                </>
            ),
            onClick: () => {
                if (!autoImportSupported) {
                    setShowPlanLimit(true);
                } else {
                    setSourceType(SourceType.PodcastRssFeed);
                    goToStage(UnifiedAddContentStages.PodcastEpisodeOptions);
                }
            }
        },
        {
            text: "I have the URLs of the podcast episodes I want to import",
            onClick: () => {
                navigateToAddManual(ManualImportPathTaken.PodcastEpisode, true, ContentType.PodcastItem);
            }
        }
    ];

    podcastOptions.push({
        text: "I want to upload an audio file",
        onClick: () => {
            if (!allowMediaUploads) {
                setMediaUploadPaywall(true);
            } else if (isAboveAudioLimit) {
                setShowAboveAudioLimit(true);
            } else {
                setFileUploadTarget(FileUploadTarget.CustomAudio);
                goToStage(UnifiedAddContentStages.ContentUpload);
            }
        }
    });

    const videoOptions: RoundedBoxCTAProps[] = [
        {
            text: "YouTube",
            onClick: () => {
                goToStage(UnifiedAddContentStages.YouTubeOptions);
            }
        },
        {
            text: "Other video platforms",
            onClick: () => {
                navigateToAddManual(ManualImportPathTaken.VideosOther, true, ContentType.Video);
            }
        }
    ];

    videoOptions.push({
        text: "Upload a video",
        onClick: () => {
            if (!allowMediaUploads) {
                setMediaUploadPaywall(true);
            } else if (isAboveVideoLimit) {
                setShowAboveVideoLimit(true);
            } else {
                setFileUploadTarget(FileUploadTarget.CustomVideo);
                goToStage(UnifiedAddContentStages.ContentUpload);
            }
        }
    });

    return (
        <>
            <V3ModalBase
                width={isManualBulkAdd ? "1200px" : isConfirmingItem ? "1000px" : "620px"}
                isOpen={modalOpen}
                onClose={modalOnCloseHandler}
                title={getModalUACTTitle(
                    stage,
                    addSourceStage,
                    sourceType,
                    importPathTaken,
                    fileUploadTarget,
                    addContentStage.stage,
                    isConfirmingItem,
                    addContentStage.isCheckingDuplicate
                )}
                useHalfContentSplit={isConfirmingItem}
                useContentBorder={stage === UnifiedAddContentStages.CollectionManualBulkAdd}
                subTitle={stage === UnifiedAddContentStages.CollectionManualBulkAdd && !!modalsubLabel ? modalsubLabel : undefined}
            >
                {stage === UnifiedAddContentStages.CollectionChooseOptions && (
                    <UACGenericOptionsScreen
                        description={`Your work samples will be added to the collection \"${collection?.label}\", and they'll also be added to your Content Archive (like everything that you add to your Authory account).`}
                        options={[
                            {
                                text: "Import or upload new content",
                                secondaryText: "Add content automatically from anywhere online or from your device ✨",
                                onClick: () => {
                                    if (!SubscriptionPlanHelpers.respectsPlanLimitations(articleCount + 1, subscriptionPlanKey)) {
                                        setShowPlanLimit(true);
                                    } else {
                                        goToStage(UnifiedAddContentStages.ChooseType);
                                    }
                                }
                            },
                            {
                                text: "Select content from my Content Archive",
                                secondaryText: "Add any content that you've previously imported to Authory",
                                disabled: !numItems,
                                tooltip: numItems === 0 ? "Add items to your content archive to enable this option" : undefined,
                                onClick: () => {
                                    goToStage(UnifiedAddContentStages.CollectionManualBulkAdd);
                                }
                            }
                        ]}
                    />
                )}
                {stage === UnifiedAddContentStages.CollectionManualBulkAdd && (
                    <ManualBulkSelectContainer
                        onBackHandler={goBack}
                        onCloseHandler={onCloseHandler}
                        collection={collection}
                        onBulkSelectionComplete={(rules) => {
                            setRulesAutoUpdateProps({ rules, target: AutoUpdateFlow.BulkAdd });
                            goToStage(UnifiedAddContentStages.AutoUpdate);
                        }}
                        subLabelCallback={setModalsubLabel}
                    />
                )}
                {stage === UnifiedAddContentStages.AutoUpdate && (
                    <AutoUpdateScreen
                        rules={rulesAutoUpdateProps.rules}
                        srcList={sources.map((src) => ({ label: src.publicationName || "", value: src.canonicalSlug }))}
                        onYesHandler={async () => {
                            // This should never be possible, but in case of bug we close the modal
                            if (!collection) {
                                onCloseHandler();
                                return;
                            }

                            await handleAutoSettingRulesForUser(rulesAutoUpdateProps.rules, collection, rules);
                        }}
                        onNoHandler={() => {
                            onAutoUpdateNextStep(false);
                        }}
                    />
                )}
                {stage === UnifiedAddContentStages.ChooseType && (
                    <UACGenericOptionsScreen
                        useCol
                        options={[
                            {
                                text: "Articles",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.Articles
                                    });
                                    goToStage(UnifiedAddContentStages.ArticleOptions);
                                },
                                customIcon: <V3IconArticle />
                            },
                            {
                                text: "Podcasts & Audios",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.Podcasts
                                    });
                                    goToStage(UnifiedAddContentStages.PodcastOptions);
                                },
                                customIcon: <V3IconPodcast />
                            },
                            {
                                text: "Videos",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.Video
                                    });
                                    goToStage(UnifiedAddContentStages.VideoOptions);
                                },
                                customIcon: <V3IconVideo />
                            },
                            {
                                text: "Social Media Posts",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.SocialMedia
                                    });
                                    navigateToAddManual(ManualImportPathTaken.SocialMedia, true, ContentType.TextPost);
                                },
                                customIcon: <V3IconPost />
                            },
                            {
                                text: "PDFs",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.PDFs
                                    });
                                    goToStage(UnifiedAddContentStages.PDFOptions);
                                },
                                customIcon: <V3IconPDF />
                            },
                            {
                                text: "Webpages & Emails",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.ScreenshotPictures
                                    });
                                    goToStage(UnifiedAddContentStages.ScreenshotOptions);
                                },
                                customIcon: <V3IconWebsiteEmail width="18" height="18" strokeWidth={"2"} />
                            },
                            {
                                text: "Books",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.Book
                                    });
                                    navigateToAddManual(ManualImportPathTaken.Book, true, ContentType.Book);
                                },
                                customIcon: <V3IconBook width="18" height="18" strokeWidth={"2"} />
                            },
                            {
                                text: "Images",
                                onClick: () => {
                                    trackEvent(UniversalAddTrackingEventNames.PickType, {
                                        type: UniversalAddPickType.Image
                                    });
                                    goToStage(UnifiedAddContentStages.Image);
                                },
                                customIcon: <V3IconImage />
                            },
                            {
                                text: "Other",
                                onClick: () => {
                                    navigateToAddManual(ManualImportPathTaken.Other, true, ContentType.Article);
                                },
                                customIcon: <V3IconWeb />
                            }
                        ]}
                        onBackHandler={stageHistory.length ? goBack : undefined}
                    />
                )}
                {stage === UnifiedAddContentStages.CollectionEditRules && collection && onRulesUpdate && (
                    <V3EditCollectionRules
                        onCloseHandler={modalOnCloseHandler}
                        rules={rules}
                        srcList={sources.map((src) => ({ label: src.publicationName || "", value: src.canonicalSlug }))}
                        collection={collection}
                        onRulesUpdate={onRulesUpdate}
                        hasRuleConflict={hasRuleConflict}
                    />
                )}
                {stage === UnifiedAddContentStages.ArticleOptions && (
                    <UACGenericOptionsScreen
                        options={[
                            {
                                text: "I want to import all articles with my byline from a  specific website",
                                secondaryText: (
                                    <>
                                        Authory will automagically find and import your articles, even if it's many 1000's{" "}
                                        <span style={{ fontSize: 20 }}>✨</span>
                                    </>
                                ),
                                onClick: () => {
                                    if (!autoImportSupported) {
                                        setShowPlanLimit(true);
                                    } else {
                                        setSourceType(SourceType.Web);
                                        goToStage(UnifiedAddContentStages.AddSource);
                                    }
                                }
                            },
                            {
                                text: "I already have the URLs of the articles that I want to import",
                                onClick: navigateToManualArticle
                            },
                            {
                                text: "I want to use Authory as my blog and publish an article",
                                skipTargetBlank: true,
                                href: `${APPLICATION_ROUTES.WRITE}?origin=${encodeURIComponent(router.asPath)}${
                                    collection ? `&collection=${encodeURIComponent(collection?.canonicalSlug)}` : ""
                                }`
                            }
                        ]}
                        onBackHandler={goBack}
                        description="There are different ways to add articles to your account. Let us find them for you, share the pieces you'd like us to import, or pen a new post."
                    />
                )}
                {stage === UnifiedAddContentStages.PodcastOptions && <UACGenericOptionsScreen options={podcastOptions} onBackHandler={goBack} />}
                {stage === UnifiedAddContentStages.PodcastEpisodeOptions && (
                    <UACGenericOptionsScreen
                        options={[
                            {
                                text: "Apple Podcast",
                                onClick: podcastOptionsOnNext
                            },
                            {
                                text: "Spotify",
                                onClick: () => {
                                    goToStage(UnifiedAddContentStages.Spotify);
                                }
                            },
                            {
                                text: "RSS",
                                onClick: podcastOptionsOnNext
                            }
                        ]}
                        onBackHandler={goBack}
                    />
                )}
                {stage === UnifiedAddContentStages.Spotify && <V3ModalUACSpotify onCloseHandler={modalOnCloseHandler} onBackHandler={goBack} />}
                {stage === UnifiedAddContentStages.VideoOptions && <UACGenericOptionsScreen options={videoOptions} onBackHandler={goBack} />}
                {stage === UnifiedAddContentStages.YouTubeOptions && (
                    <UACGenericOptionsScreen
                        options={[
                            {
                                text: "I want to import an entire channel/profile/playlist",
                                secondaryText: (
                                    <>
                                        Authory will automagically find and import your videos, even if it's many 1000's{" "}
                                        <span style={{ fontSize: 20 }}>✨</span>
                                    </>
                                ),
                                onClick: () => {
                                    if (!autoImportSupported) {
                                        setShowPlanLimit(true);
                                    } else {
                                        setSourceType(SourceType.Youtube);
                                        goToStage(UnifiedAddContentStages.AddSource);
                                    }
                                }
                            },
                            {
                                text: "I have the URLs of the videos I want to import",
                                onClick: () => {
                                    navigateToAddManual(ManualImportPathTaken.YoutubeOther, false, ContentType.Video);
                                }
                            }
                        ]}
                        onBackHandler={goBack}
                    />
                )}
                {stage === UnifiedAddContentStages.PDFOptions && (
                    <UACGenericOptionsScreen
                        options={[
                            {
                                text: "Upload files",
                                onClick: () => {
                                    setFileUploadTarget(FileUploadTarget.PDF);
                                    goToStage(UnifiedAddContentStages.ContentUpload);
                                }
                            },
                            {
                                text: "Import from URL",
                                onClick: () => {
                                    setIsPDFURLImport(true);
                                    navigateToAddManual(ManualImportPathTaken.PDFs, false, ContentType.PdfFile);
                                }
                            }
                        ]}
                        onBackHandler={goBack}
                    />
                )}
                {stage === UnifiedAddContentStages.ScreenshotOptions && (
                    <UACGenericOptionsScreen
                        options={[
                            {
                                text: "Email",
                                onClick: () => {
                                    goToStage(UnifiedAddContentStages.AddContentEmail);
                                }
                            },
                            {
                                text: "Webpage",
                                onClick: () => {
                                    setFileUploadTarget(FileUploadTarget.Website);
                                    goToStage(UnifiedAddContentStages.WebsiteAddURL);
                                }
                            }
                        ]}
                        onBackHandler={goBack}
                    />
                )}
                {stage === UnifiedAddContentStages.AddSource && (
                    <AddSource
                        onCloseHandler={modalOnCloseHandler}
                        onAutommatedImportSubmit={async (data) => {
                            const col = await onAutommatedImportSubmit(data);
                            // If we're on collection context, we highjack the add source flow to do the auto-update flow and then return here
                            // This is done like this because the UnifiedAddContentStages.AddSource is a legacy component that was eventually plugged in into the "universal add content" flow
                            if (collection && !collection.default) {
                                const newRule = [
                                    {
                                        id: "foo", // not relevant
                                        type: CollectionRuleType.Source,
                                        params: [col?.canonicalSlug || ""]
                                    }
                                ];
                                setRulesAutoUpdateProps({ rules: newRule, target: AutoUpdateFlow.Source });
                                goToStage(UnifiedAddContentStages.AutoUpdate);
                            }
                        }}
                        startingStage={autoUpdateStepCompleted ? AddSourceWizardStage.Completed : undefined}
                        sources={sources}
                        trackEvent={trackEvent}
                        sourceType={sourceType}
                        names={names}
                        onAliasesSubmit={onAliasesSubmit}
                        onBackHandler={goBack}
                        onSourceExists={onSourceExists}
                        onStageChange={setAddSourceStage}
                        onGoToAddManual={(pathTaken, isSnippetImport, targetContentType, url) => {
                            navigateToAddManual(pathTaken, isSnippetImport, targetContentType);
                            if (url) setSuggestedUrl(url);
                        }}
                        onSourceTypeChange={setSourceType}
                        collection={autoRulesSaved ? collection : undefined}
                        hideBackButton={!stageHistory.length}
                    />
                )}
                {stage === UnifiedAddContentStages.AddManual && (
                    <AddContent
                        usedPlugin={usedPlugin}
                        sources={sources}
                        isPDFURLImport={isPDFURLImport}
                        targetContentType={targetContentType}
                        isSnippetImport={isSnippetImport}
                        onFileChoosenHandler={onFileChoosenHandler}
                        onCloseHandler={modalOnCloseHandler}
                        onFindContent={onFindContent}
                        onGetMediaUploadSignedUrl={onGetMediaUploadSignedUrl}
                        onFindContentSnippets={onFindContentSnippets}
                        onFindDuplicate={onFindDuplicate}
                        onReplaceDuplicate={async (item) => {
                            const replaced = await onReplaceDuplicate(item);

                            if (replaced) {
                                trackEvent(UniversalAddTrackingEventNames.DuplicateReplaced, {
                                    slug: replaced.slug,
                                    type: replaced.type
                                });
                                setItemAdded((it) => [...it, replaced]);
                            }

                            return replaced;
                        }}
                        onContentAddComplete={() => {
                            // If we're on collection context, we highjack the add content flow to do the auto-update flow and then return here
                            // This is done like this because the UnifiedAddContentStages.AddManual is a legacy component that was eventually plugged in into the "universal add content" flow
                            if (collection && !collection.default && !!newSources.length) {
                                const newRule = newSources.map((src) => ({
                                    id: "foo", // not relevant
                                    type: CollectionRuleType.Source,
                                    params: [src]
                                }));
                                setRulesAutoUpdateProps({ rules: newRule, target: AutoUpdateFlow.Item });
                                goToStage(UnifiedAddContentStages.AutoUpdate);
                            }
                        }}
                        onManualImportSubmit={async (data) => {
                            const item = await onManualImportSubmit(data);

                            if (item) {
                                trackEvent(UniversalAddTrackingEventNames.ItemAdded, {
                                    slug: item.slug,
                                    type: item.type
                                });
                                setItemAdded((it) => [...it, item]);
                            }

                            // Save the new sources to ask for rules later
                            if (data.newSource && item) {
                                setNewSources((srcs) => [item?.source.canonicalSlug, ...srcs]);
                            }

                            return item;
                        }}
                        onTemporaryImageUploadHandler={onTemporaryImageUploadHandler}
                        onWebsiteImport={onWebsiteImport}
                        onStageChange={setAddContentStage}
                        onBackHandler={() => {
                            setIsPDFURLImport(false);
                            setSuggestedUrl("");
                            goBack();
                        }}
                        trackEvent={trackEvent}
                        importPathTaken={importPathTaken}
                        suggestedUrl={suggestedUrl}
                        importItems={importItems}
                        itemAdded={itemAdded}
                        startingStage={autoUpdateStepCompleted ? AddContentWizardStage.AddContentComplete : undefined}
                        onJumpToPDF={() => goToStage(UnifiedAddContentStages.PDFOptions)}
                    />
                )}
                {stage === UnifiedAddContentStages.ContentUpload && (
                    <AddContentUpload
                        onBackHandler={goBack}
                        onFilesChoosen={(files: File[]) => {
                            const isUploadTargetPDF = fileUploadTarget === FileUploadTarget.PDF;
                            const isUploadTargetWebsite = fileUploadTarget === FileUploadTarget.Website;
                            const isUploadTargetEmail = fileUploadTarget === FileUploadTarget.Email;
                            const isUploadTargetCustomVideo = fileUploadTarget === FileUploadTarget.CustomAudio;
                            const isUploadTargetCustomAudio = fileUploadTarget === FileUploadTarget.CustomVideo;

                            if (isUploadTargetPDF || isUploadTargetCustomVideo || isUploadTargetCustomAudio) {
                                const itemsList: ImportItem[] = [];

                                for (let index = 0; index < files.length; index++) {
                                    const file = files[index];

                                    const item: ImportItem = {
                                        contentRes: undefined,
                                        url: null,
                                        id: makeIdFromLength(10),
                                        files: [file],
                                        fileUploadTarget: fileUploadTarget
                                    };

                                    itemsList.push(item);
                                }

                                setImportItems(itemsList);
                            } else if (isUploadTargetWebsite || isUploadTargetEmail) {
                                const item: ImportItem = {
                                    contentRes: undefined,
                                    url: null,
                                    id: makeIdFromLength(10),
                                    files: files,
                                    fileUploadTarget: fileUploadTarget
                                };

                                setImportItems([item]);
                            }

                            navigateToAddManual(ManualImportPathTaken.PDFs, false, ContentType.PdfFile);
                        }}
                        fileUploadTarget={fileUploadTarget}
                        onCloseHandler={modalOnCloseHandler}
                    />
                )}
                {stage === UnifiedAddContentStages.WebsiteAddURL && (
                    <AddContentAddURL
                        onBackHandler={goBack}
                        onManualArticleSelect={navigateToManualArticle}
                        onUrlSubmitted={(url) => {
                            setImportItems([
                                {
                                    url: url,
                                    contentRes: undefined,
                                    id: makeIdFromLength(10),
                                    fileUploadTarget: FileUploadTarget.Website
                                }
                            ]);
                            navigateToAddManual(ManualImportPathTaken.WebsiteScreenshot, false, ContentType.Website);
                        }}
                        onFilesChoosen={(files: File[]) => {
                            const item: ImportItem = {
                                contentRes: undefined,
                                url: null,
                                id: makeIdFromLength(10),
                                files: files,
                                fileUploadTarget: FileUploadTarget.Website
                            };

                            setImportItems([item]);
                            navigateToAddManual(ManualImportPathTaken.WebsiteScreenshot, false, ContentType.Website);
                        }}
                    />
                )}
                {stage === UnifiedAddContentStages.Image && (
                    <AddContentImage
                        onBackHandler={goBack}
                        onFilesChoosen={(files: File[]) => {
                            const item: ImportItem = {
                                contentRes: undefined,
                                url: null,
                                id: makeIdFromLength(10),
                                files: files,
                                fileUploadTarget: FileUploadTarget.Image
                            };

                            setImportItems([item]);
                            navigateToAddManual(ManualImportPathTaken.Image, false, ContentType.Image);
                        }}
                    />
                )}
                {stage === UnifiedAddContentStages.AddContentEmail && (
                    <AddContentEmail
                        onBackHandler={goBack}
                        sourcesOptions={sources.map((src) => ({ value: src.canonicalSlug, text: src.publicationName }))}
                        onAddContentEmailFormSubmit={async (data) => {
                            if (!data.newSource) {
                                setEmailSourceSlug(data.sourceSlug || "");
                            } else {
                                const newSource = await onEmailSourceSubmit(data.name);

                                if (newSource) {
                                    setEmailSourceSlug(newSource.canonicalSlug);
                                }
                            }

                            goToStage(UnifiedAddContentStages.AddContentEmailFinished);
                        }}
                        onFilesChoosen={(files: File[]) => {
                            const item: ImportItem = {
                                contentRes: undefined,
                                url: null,
                                id: makeIdFromLength(10),
                                files: files,
                                fileUploadTarget: FileUploadTarget.Email
                            };

                            setImportItems([item]);
                            navigateToAddManual(ManualImportPathTaken.WebsiteScreenshot, false, ContentType.Email);
                        }}
                    />
                )}
                {stage === UnifiedAddContentStages.AddContentEmailFinished && (
                    <AddContentEmailFinished
                        email={sources.find((src) => src.canonicalSlug === emailSourceSlug)?.importEmail || ""}
                        sourceName={sources.find((src) => src.canonicalSlug === emailSourceSlug)?.publicationName || ""}
                        onCloseHandler={modalOnCloseHandler}
                        onBackHandler={goBack}
                    />
                )}
            </V3ModalBase>
            {manualImportFlowExitDialog && (
                <V3ConfirmationModal
                    onSubmit={() => {
                        onCloseHandler();
                    }}
                    setModalOpen={setManualImportFlowExitDialog}
                    modalOpen
                    title={"Close this window and cancel importing?"}
                    btnLabel={"Yes"}
                    cancelBtbLabel="No"
                    useIcon
                    variant={V3ModalIconSegmentVariant.warning}
                    description={"If you close the window, the import process will be cancelled."}
                />
            )}
            {showAboveAudioLimit && (
                <V3ConfirmationModal
                    onSubmit={() => {
                        setShowAboveAudioLimit(false);
                    }}
                    setModalOpen={setShowAboveAudioLimit}
                    modalOpen={true}
                    title={"Audio limit exceded"}
                    useIcon
                    cancelBtbLabel="Close"
                    variant={V3ModalIconSegmentVariant.warning}
                    description={getMaxLimitUsageMessage(mediaUsage.audio.limit, mediaUsage.audio.used)}
                />
            )}
            {showAboveVideoLimit && (
                <V3ConfirmationModal
                    onSubmit={() => {
                        setShowAboveVideoLimit(false);
                    }}
                    setModalOpen={setShowAboveVideoLimit}
                    modalOpen={true}
                    title={"Video limit exceded"}
                    useIcon
                    cancelBtbLabel="Close"
                    variant={V3ModalIconSegmentVariant.warning}
                    description={getMaxLimitUsageMessage(mediaUsage.video.limit, mediaUsage.video.used)}
                />
            )}
            {showMediaUploadPaywall && (
                <V3ConfirmationModal
                    onSubmit={() => {
                        setMediaUploadPaywall(false);
                        router.push(APPLICATION_ROUTES.SETTINGS_BILLING);
                    }}
                    setModalOpen={setMediaUploadPaywall}
                    modalOpen={true}
                    title={isOnTrial ? "Enable media uploads during your trial" : "Media uploads are not included in your plan"}
                    useIcon
                    cancelBtbLabel="Close"
                    btnLabel={isOnTrial ? "Add payment details" : "View plans"}
                    variant={V3ModalIconSegmentVariant.warning}
                    description={
                        <V3BodyGrey48>
                            {isOnTrial ? (
                                <>
                                    To upload media files, you need to be on the Standard or Professional plans and add your payment details. Your
                                    free trial will continue, and you won’t be charged until it ends.
                                </>
                            ) : (
                                <>Upgrade to the Standard or Professional plan to upload media files to your portfolio.</>
                            )}
                        </V3BodyGrey48>
                    }
                />
            )}
        </>
    );
};
